import React, { useState } from "react";
import CoursePaymentCard from "../../features/CoursePaymentPage/CoursePaymentCard/CoursePaymentCard";
import CoursePaymentForm from "../../features/CoursePaymentPage/CoursePaymentForm/CoursePaymentForm";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import { useForm } from "@mantine/form";
import { useAppContext } from "../../context/AppState/AppContext";
import axios from "axios";
import "./CoursePaymentPage.css";
import { openUrl } from "../../utils/openUrl";
import { useNavigate } from "react-router-dom";
import showNotification from "../../utils/showNotification";
import SectionTitle from "../../components/SectionTitle/SectionTitle";
import { isValidPhoneNumber } from "react-phone-number-input";
import DecorativeCircle from "../../components/DecorativeCircle/DecorativeCircle";
import TinyDecorativeCircle from "../../components/TinyDecorativeCircle/TinyDecorativeCircle";
import { calculateDiscountedPrice } from "../../utils/calculateDiscountedPrice";

function validateEmail(_email) {
   const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

   if (emailPattern.test(_email.trim())) {
      return null;
   } else {
      return "E-mail inválido";
   }
}

const CoursePaymentPage = ({ isGrand = false }) => {
   const [discountInfo, setDiscountInfo] = useState({
      percentage: 0,
      active: false,
      code: "",
      _id: "",
   });
   const [isRegistered, setIsRegistered] = useState(false);
   const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
   const { paymentData, setPaymentData, isUSD, officialUSDValue } = useAppContext();




   const navigate = useNavigate()

   const [loading, setLoading] = useState(false);

   const form = useForm({
      validateInputOnChange: true,
      initialValues: {
         name: "",
         email: "",
         emailConfirmation: "",
         apellido: "",
         telefono: "",
         dni: ""
      },
      validate: {
         name: (value) => {
            if (isRegistered) return false;
            const isValid = /^[a-zA-Z\s]+$/.test(value);
            return isValid && value.length >= 2
               ? null
               : "No debe tener al menos 2 letras y no contener puntos, guiones ni comas";
         },
         email: (value) => {
            return validateEmail(value);
         },
         emailConfirmation: (value) => {
            return validateEmail(value);
         },
         apellido: (value) => {
            if (isRegistered) return false;
            const isValid = /^[a-zA-Z\s]+$/.test(value);
            return isValid && value.length >= 2
               ? null
               : "No debe tener al menos 2 letras y no contener puntos, guiones ni comas";
         },
         telefono: (value) => {
            if (isRegistered) return false;
            if (typeof value === "string")
               return !isValidPhoneNumber(value)
            else return true
         },
         dni: value => {
            if (!value) return "Debes completar con tu número de identificación"
            if (value.length <= 4) return "El número de identificación es demasiado corto."
            if (value.length > 19) return "El número de identificación es demasiado largo."
         },
      },
   });

   if (!paymentData?.id) return navigate(-1)

   // Cuando no hay nada que cobrar, elegir forma de pago es un tramite vacio:
   // la persona tiene que decidir COMO pagar cero. Asi que se saltea.
   //
   // La condicion es el TOTAL en cero, no "hay cupon": un cupon del 30% deja un
   // precio que si hay que cobrar. Un 100% y un curso gratis caen los dos aca.
   //
   // Las suscripciones quedan afuera a proposito: no ofrecen transferencia y su
   // alta es un flujo recurrente, no una inscripcion suelta.
   const precioLista = Number(paymentData.price) || 0;
   const precioFinal = discountInfo.active
      ? calculateDiscountedPrice(precioLista, discountInfo.percentage)
      : precioLista;
   const esGratis = !paymentData?.isSubscription && precioFinal <= 0;

   const handlePaymentMethodChange = (id) => {
      setSelectedPaymentMethod(id);
   };

   const buildPostData = () => {
      const { id, name, price, isSubscription, isPack, division } = paymentData;
      const postData = {
         data: {
            description: name, // Course, pack or subscription name
            amount: price,
            agreedAmount: price,
            source: isSubscription ? "SUBSCRIPTION" : (isPack ? "PACK" : "ACADEMIC_UNIT"),
            division,
            name: form.values.name.trim(),
            lastname: form.values.apellido.trim(),
            email: form.values.email.toLowerCase().trim(),
            dni: form.values.dni.trim(),
            phone: form.values.telefono.trim(),
            // Un pack se cobra con packId: el backend inscribe en cada curso del pack
            academicUnitId: (isSubscription || isPack) ? null : id,
            packId: isPack ? id : null,
            subscriptionId: isSubscription ? id : null,
            discount: null,
            currency: isUSD ? 'USD' : 'ARS'
         },
      };

      // If discount is active, add it to the post data
      if (discountInfo.active) {
         postData.data.agreedAmount = calculateDiscountedPrice(price, discountInfo.percentage);
         postData.data.discount = {
            _id: discountInfo._id,
            percent: discountInfo.percentage,
            amount: price - postData.data.agreedAmount,
         };
      }

      // If the user is already registered, add a flag to the post data
      if (isRegistered) {
         postData.data.is_registered = true;
      }

      return postData;
   }

   async function handleSubscriptionPayment(_data, _selectedSubscriptionPlan) {
      // Add subscription plan data to post data

      _data.data.subscriptionPlanId = _selectedSubscriptionPlan;
      _data.data.subscriptionData = paymentData.subscriptionPlans.find(
         (plan) => plan.subscriptionPlanId === _selectedSubscriptionPlan
      );
      if (!isUSD) {
         // Since the subscription is in USD, we need to transform everything to USD
         _data.data.currency = "USD";

         const subscriptionAmount = !paymentData.isSubscription
            ? _data.data.subscriptionData.amount_per_cycle * _data.data.subscriptionData.total_cycles
            : Number(Number(paymentData?.price / officialUSDValue).toFixed(2))
         _data.data.amount = subscriptionAmount;
         _data.data.agreedAmount = subscriptionAmount;
         if (discountInfo.active) {
            _data.data.agreedAmount = calculateDiscountedPrice(subscriptionAmount, discountInfo.percentage);
            _data.data.discount = {
               ..._data.data.discount,
               amount: subscriptionAmount - _data.data.agreedAmount,
            };
         }
      }

      // Today, it only supports PayPal

      if (selectedPaymentMethod === 'paypal') {
         return axios.post(
            `${process.env.REACT_APP_BACKEND_SERVER}/payment/subscription/paypal`,
            _data
         );
      }
      return axios.post(
         `${process.env.REACT_APP_BACKEND_SERVER}/payment/subscription/dlocalgo`,
         _data
      );


   }

   async function handleUniquePayment(_data) {
      return axios.post(
         `${process.env.REACT_APP_BACKEND_SERVER}/payment/${selectedPaymentMethod}`,
         _data
      );
   }

   const handlePaymentSubmit = async (selectedSubscriptionPlan) => {
      const isValid = form.isValid();
      if (form.values.email !== form.values.emailConfirmation) {
         form.setFieldError("emailConfirmation", "Los e-mails no coinciden");
         return;
      }
      // Gratis: se inscribe de una, sin pasar por la pantalla de transferencia.
      // Por dentro usa el mismo endpoint /payment/transfer que usaria el boton
      // "Finalizar proceso" de esa pantalla cuando no se sube comprobante, asi
      // que para el backend es una inscripcion por transferencia como cualquier
      // otra, con agreedAmount en cero.
      if (isValid && esGratis) {
         const postData = buildPostData();
         setPaymentData({ ...paymentData, postData, timestamp: new Date().getTime() });

         try {
            setLoading(true);
            const formData = new FormData();
            formData.append("postData", JSON.stringify(postData));
            const response = await axios.post(
               `${process.env.REACT_APP_BACKEND_SERVER}/payment/transfer`,
               formData,
               { headers: { "Content-Type": "multipart/form-data" } },
            );
            openUrl(response.data.data, false);
         } catch (err) {
            showNotification({
               color: "red",
               status: "error",
               title: "No pudimos completar la inscripción.",
               message: `Detalles: ${err?.response?.data?.error || "Sin detalles"}`,
            });
         } finally {
            setLoading(false);
         }
         return;
      }

      if (isValid && selectedPaymentMethod !== null) {
         const postData = buildPostData();

         const timestamp = new Date().getTime();
         setPaymentData({ ...paymentData, postData, timestamp });

         if (selectedPaymentMethod === "transfer") {
            const transferBankNavigate = isGrand ? "/grand/transferencia-bancaria" : "/transferencia-bancaria"
            navigate(transferBankNavigate);
            return;
         }

         try {
            setLoading(true);

            let response;
            if (selectedSubscriptionPlan !== "UNIQUE_PAYMENT") {
               response = await handleSubscriptionPayment(postData, selectedSubscriptionPlan);
            } else {
               response = await handleUniquePayment(postData);
            }

            openUrl(response.data.data, false);
         } catch (error) {
            console.error("Error al procesar el pago:", error);
         } finally {
            setLoading(false);
         }
      } else {
         showNotification({
            color: "red",
            status: "error",
            title: "Pago no procesado.",
            message: `Debe completar sus datos y seleccionar un medio de pago para avanzar.`,
         });
      }
   };



   return (
      <MainLayout overflow="hidden">
         <section className={`coursePaymentPage-section ${isGrand ? "coursePaymentPage-section_grand" : ""}`} style={{ position: "relative" }}>
            <SectionTitle upText='¿Todo listo?' downText='Un último paso para formar parte de la comunidad' />
            <DecorativeCircle size={100} top={-20} left={-20} opacity={0.1} shadow={1} />
            <DecorativeCircle size={150} bottom={-220} left={300} opacity={0.1} shadow={1} />
            <DecorativeCircle size={50} top={-20} left={800} opacity={0.2} shadow={4} />
            <DecorativeCircle size={100} top={0} right={-50} opacity={0.2} shadow={2} />
            <TinyDecorativeCircle size={12} top={50} right={350} opacity={.5} color={1} />
            <TinyDecorativeCircle size={12} top={300} left={70} opacity={0.3} color={1} />
            <TinyDecorativeCircle size={32} top={80} left={500} opacity={.3} color={3} />
         </section>
         <div className="coursePaymentPage-layout coursePaymentPage-section">
            <div className="coursePaymentPage-main">
               <CoursePaymentForm
                  isGrand={isGrand}
                  form={form}
                  isRegistered={isRegistered}
                  setIsRegistered={setIsRegistered}
               />
            </div>
            <aside className="coursePaymentPage-aside">
               <CoursePaymentCard
                  isGrand={isGrand}
                  loadingButton={loading}
                  selectedPaymentMethod={selectedPaymentMethod}
                  onPaymentMethodChange={handlePaymentMethodChange}
                  onSubmit={handlePaymentSubmit}
                  discountInfo={discountInfo}
                  setDiscountInfo={setDiscountInfo}
                  paymentDataId={paymentData.id}
                  paymentDataName={paymentData.name}
                  paymentDataImage={paymentData.image}
                  paymentDataPrice={paymentData.price}
                  subscriptionPlans={paymentData.subscriptionPlans}
                  isSubscription={paymentData?.isSubscription}
                  esGratis={esGratis}
               />
            </aside>
         </div>
      </MainLayout>
   );
};

export default CoursePaymentPage;
